import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { numero, t } from '../../i18n/idioma';
import { useBuffs, useEfeitosDe, type BuffAtivo } from '../../world/buffs';
import { arteDoPergaminho, tempoDoPergaminho, usePergaminhoXp, type PergaminhoXp } from '../../world/pergaminho';
import type { AlvoVitais, WorldStatus } from '../../world/types';
import { useDica } from './Dica';
import { DicaEfeito, totalDe, type EfeitoNaBarra } from './dicasConteudo';
import { aoRestaurar, gravarJanela, lerJanela, type PosJanela } from './posicoes';
import { useModo } from './useModoTela';

export type Marca = { pct: number; dica: string };

/** Uma barra: ícone, trilho com o número dentro e as marcas da cura e da reserva de mana. */
export function Vital({ cls, icone, v, max, nome, marcas = [] }: {
  cls: string; icone?: ReactNode; v: number; max: number; nome: string; marcas?: readonly Marca[];
}) {
  const dica = [t('hud.barra.vital', { nome, v: numero(v), max: numero(max) }), ...marcas.map(m => m.dica)].join(' · ');
  return (
    <span className={`bb-v bb-v--${cls}`} title={dica}>
      {icone}
      <span className="bb-v__t" role="meter" aria-label={nome} aria-valuemin={0} aria-valuemax={max} aria-valuenow={v}>
        <i style={{ width: `${max ? Math.min(100, (v / max) * 100) : 0}%` }} />
        {marcas.map(m => <s key={`${m.pct}-${m.dica}`} style={{ left: `${m.pct}%` }} />)}
        <b>{numero(Math.round(v))}/{numero(Math.round(max))}</b>
      </span>
    </span>
  );
}

/**
 * As barras do personagem (techspec-hud-v3 §19, §20 e §22). Os seus efeitos saíram daqui para o `PainelEfeitos`.: saíram da barra de skills para uma placa solta e estreita,
 * sem ícones nem as marcas da cura e da reserva de mana. Desde a §23 ela mora na grade dos botões flutuantes
 * (`BotoesFlutuantes`, duas colunas): arrasta, mostra as casas livres, encaixa e guarda a casa como os botões.
 * Aqui fica só o conteúdo; a moldura é a `.v3-fab__placa`.
 */
export function PlacaVida({ status }: { status: WorldStatus | null }) {
  const v = status ?? { hp: 0, hpMax: 0, mana: 0, manaMax: 0, stam: 0, stamMax: 0 };
  return (
    <div className="vt">
      <Vital cls="hp" v={v.hp} max={v.hpMax} nome={t('mundo.vida')} />
      <Vital cls="mn" v={v.mana} max={v.manaMax} nome={t('mundo.mana')} />
      <Vital cls="vg" v={v.stam} max={v.stamMax} nome={t('mundo.vigor')} />
    </div>
  );
}

/** "2:05", "45s": o que falta do efeito, curto para caber embaixo do ícone. */
function falta(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

/** Tipos de debuff (o valor tira do atributo). */
const DEBUFFS = new Set(['forca-menos', 'destreza-menos', 'inteligencia-menos']);
/** Magias que põem vários tipos de uma vez: um ícone só, o da magia. */
const AGRUPA = new Set(['bless', 'curse']);

/** Junta Bless e Curse (três tipos cada) num ícone; o resto, um por tipo. */
function iconesDe(lista: readonly BuffAtivo[], agora: number): EfeitoNaBarra[] {
  const out: EfeitoNaBarra[] = [];
  const grupos = new Map<string, EfeitoNaBarra>();
  for (const b of lista) {
    if (b.fim <= agora) continue;
    const debuff = DEBUFFS.has(b.tipo);
    if (AGRUPA.has(b.item)) {
      const g = grupos.get(b.item);
      if (g) { grupos.set(b.item, { ...g, fim: Math.min(g.fim, b.fim) }); continue; }
      const novo: EfeitoNaBarra = { chave: b.item, tipo: b.tipo, item: b.item, icone: `/sprites/itens/buffs/${b.item}.png`,
        nome: t(`buff.tipo.${b.item}`), valor: b.valor, total: totalDe(b), debuff, fim: b.fim };
      grupos.set(b.item, novo);
      continue;
    }
    out.push({ chave: b.tipo, tipo: b.tipo, item: b.item, icone: `/sprites/itens/buffs/${b.tipo}.png`,
      nome: t(`buff.tipo.${b.tipo}`), valor: b.valor, total: totalDe(b), debuff, fim: b.fim });
  }
  return [...grupos.values(), ...out];
}

/** O número em cima do ícone: o que sobra do estoque (Reflexão "4/6", Reativa "22") ou o tempo. */
function rotuloDoIcone(e: EfeitoNaBarra, agora: number): string {
  if (e.tipo === 'reflexao-magica') return `${numero(e.valor)}/${numero(Math.max(e.total, e.valor))}`;
  if (e.tipo === 'armadura-reativa') return numero(e.valor);
  return falta(e.fim - agora);
}

function IconeDeEfeito({ e, agora }: { e: EfeitoNaBarra; agora: number }) {
  const dica = useDica(() => <DicaEfeito e={e} />, { toque: true });
  const bonus = e.valor > 0 ? (e.debuff ? ` −${numero(e.valor)}` : ` +${numero(e.valor)}`) : '';
  return (
    <span
      className={`vt__buff${e.debuff ? ' is-debuff' : ''}`}
      role="listitem"
      tabIndex={0}
      aria-label={t('buff.dica', { efeito: e.nome, bonus, tempo: falta(e.fim - agora) })}
      {...dica}
    >
      <img src={e.icone} alt="" draggable={false} />
      <small>{rotuloDoIcone(e, agora)}</small>
    </span>
  );
}

/**
 * O Pergaminho de Experiência (techspec-pergaminhos-loja §03): o ícone com o tempo de combate que falta e um ❚❚ quando o
 * relógio está parado (na cidade, na vila de casas ou sem atacar). Não some na morte; só quando o tempo acaba.
 */
function IconePergaminhoXp({ p }: { p: PergaminhoXp }) {
  const dica = useDica(() => <DicaPergaminhoXp p={p} />, { toque: true });
  return (
    <span
      className={`vt__buff vt__buff--perg${p.corre ? '' : ' is-parado'}`}
      role="listitem"
      tabIndex={0}
      aria-label={t(p.corre ? 'pergaminho.hud.ariaCorre' : 'pergaminho.hud.ariaParado', { pct: p.pct, tempo: tempoDoPergaminho(p.segundos) })}
      {...dica}
    >
      <img src={arteDoPergaminho('0x227a')} alt="" draggable={false} />
      {!p.corre && <i className="vt__buff-pausa" aria-hidden="true">❚❚</i>}
      <small>{tempoDoPergaminho(p.segundos)}</small>
    </span>
  );
}

function DicaPergaminhoXp({ p }: { p: PergaminhoXp }) {
  return (
    <div className="dk dk--efeito dk--perg">
      <div className="dk__cab dk__cab--curta">
        <img src={arteDoPergaminho('0x227a')} alt="" draggable={false} />
        <div className="dk__nome">{t('pergaminho.hud.nome')}</div>
      </div>
      <div className="dk__destaque"><b>+{numero(p.pct)}%</b><small>{t('pergaminho.hud.xp')}</small></div>
      <ul className="dk__linhas">
        <li>{t(p.corre ? 'pergaminho.hud.correndo' : 'pergaminho.hud.parado')}</li>
        <li>{t('pergaminho.hud.morte')}</li>
      </ul>
      <hr />
      <div className="dk__pe"><span>{t('pergaminho.hud.falta')} <b className="dk__tempo">{tempoDoPergaminho(p.segundos)}</b></span></div>
    </div>
  );
}

/**
 * Os ícones de buff e debuff (techspec-pocoes e techspec-magias-de-efeito), com o tempo por cima do ícone para ocupar
 * menos (Renato, 28/10/2026). São os ícones da barra de buffs da UO: verde é bom, vermelho é ruim. Passar o mouse
 * (ou o toque longo) abre a caixa com o valor de agora, de onde veio e o tempo (techspec-tooltips-e-efeitos).
 */
function LinhaEfeitos({ lista, rotulo, className = 'vt__buffs' }: { lista: readonly BuffAtivo[]; rotulo: string; className?: string }) {
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    if (lista.length === 0) return;
    setAgora(Date.now());
    const id = window.setInterval(() => setAgora(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [lista]);
  const icones = iconesDe(lista, agora);
  if (icones.length === 0) return null;
  return (
    <div className={className} role="list" aria-label={rotulo}>
      {icones.map(e => <IconeDeEfeito key={e.chave} e={e} agora={agora} />)}
    </div>
  );
}

/**
 * Os seus efeitos numa barra solta (Renato, 28/10/2026): saiu de cima da placa de vida e o jogador arrasta para onde
 * ficar melhor. A posição fica no navegador, como a das janelas (posicoes.ts, chave "efeitos"); sem posição, no alto,
 * à esquerda do centro. Arrasta pela alça; os ícones continuam com a caixa de dica.
 */
export function PainelEfeitos() {
  const lista = useBuffs();
  const perg = usePergaminhoXp();
  const modo = useModo();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<PosJanela | null>(() => lerJanela(modo, JANELA_EFEITOS));
  useEffect(() => { setPos(lerJanela(modo, JANELA_EFEITOS)); }, [modo]);
  useEffect(() => aoRestaurar(() => setPos(null)), []);

  const arrastar = (ev: ReactPointerEvent<HTMLElement>) => {
    if (ev.button !== 0) return;
    const el = ref.current;
    if (!el) return;
    ev.preventDefault();
    const caixa = el.getBoundingClientRect();
    const dx = ev.clientX - caixa.left;
    const dy = ev.clientY - caixa.top;
    let ultima: PosJanela = { x: caixa.left, y: caixa.top };
    const mover = (e: PointerEvent) => {
      const w = el.offsetWidth, h = el.offsetHeight;
      ultima = {
        x: Math.max(0, Math.min(window.innerWidth - w, e.clientX - dx)),
        y: Math.max(0, Math.min(window.innerHeight - h, e.clientY - dy))
      };
      setPos(ultima);
    };
    const soltar = () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', soltar);
      el.classList.remove('is-arrastando');
      gravarJanela(modo, JANELA_EFEITOS, ultima);
    };
    el.classList.add('is-arrastando');
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    window.addEventListener('pointercancel', soltar);
  };

  if (lista.length === 0 && perg.segundos <= 0) return null;
  const estilo = pos ? { left: pos.x, top: pos.y } : undefined;
  return (
    <div ref={ref} className={`vt-efeitos${pos ? '' : ' is-padrao'}`} style={estilo}>
      <span className="vt-efeitos__alca" title={t('buff.arrastar')} aria-hidden="true" onPointerDown={arrastar} />
      {perg.segundos > 0 && (
        <div className="vt-efeitos__lista" role="list" aria-label={t('pergaminho.hud.nome')}><IconePergaminhoXp p={perg} /></div>
      )}
      <LinhaEfeitos lista={lista} rotulo={t('buff.placa')} className="vt-efeitos__lista" />
    </div>
  );
}

const JANELA_EFEITOS = 'efeitos';

/** Os efeitos de outro jogador (o "bo" do servidor) numa linha própria: a placa do grupo (techspec-grupo-placas). */
export function LinhaEfeitosDe({ id, rotulo, className }: { id: number | null; rotulo: string; className?: string }) {
  return <LinhaEfeitos lista={useEfeitosDe(id)} rotulo={rotulo} className={className} />;
}

/** Os efeitos do alvo (o "bo" do servidor), em cima da placa dele. */
function LinhaEfeitosDoAlvo({ id }: { id: number }) {
  return <LinhaEfeitos lista={useEfeitosDe(id)} rotulo={t('buff.placaAlvo')} />;
}

/**
 * As barras de quem o jogador está atacando (techspec-hud-v3 §19). Só existe em combate. Como a placa de vida, mora na
 * grade dos botões flutuantes (§25): arrasta, mostra as casas livres, encaixa e guarda a casa. Mob não manda mana nem
 * vigor: fica só a vida. Aqui fica só o conteúdo; a moldura é a `.v3-fab__placa--alvo`.
 */
export function PlacaAlvo({ alvo, retratoDe }: {
  alvo: AlvoVitais;
  /** O retrato à esquerda (Renato, 29/10/2026), como nas placas do grupo. Sem ele, a placa fica só com as barras. */
  retratoDe?: (canvas: HTMLCanvasElement, id: number) => () => void;
}) {
  const cv = useRef<HTMLCanvasElement>(null);
  const pintar = useRef(retratoDe);
  pintar.current = retratoDe;
  useEffect(() => {
    const c = cv.current;
    if (!c || !pintar.current) return;
    return pintar.current(c, alvo.id);
  }, [alvo.id]);
  const barras = (
    <div className="vt">
      <span className="vt__nome" data-noto={alvo.notoriety}>{alvo.nome}</span>
      <Vital cls="hp" v={alvo.hits} max={alvo.hitsMax} nome={t('mundo.vida')} />
      {alvo.manaMax > 0 && <Vital cls="mn" v={alvo.mana} max={alvo.manaMax} nome={t('mundo.mana')} />}
      {alvo.stamMax > 0 && <Vital cls="vg" v={alvo.stam} max={alvo.stamMax} nome={t('mundo.vigor')} />}
    </div>
  );
  if (!retratoDe)
    return <div className="vt-alvo vt-alvo--sem">{barras}<LinhaEfeitosDoAlvo id={alvo.id} /></div>;
  return (
    <div className={`vt-alvo is-${alvo.kind}`}>
      <canvas ref={cv} className="vt-alvo__retrato" width={40} height={40} aria-hidden="true" />
      {barras}
      <LinhaEfeitosDoAlvo id={alvo.id} />
    </div>
  );
}
